import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  Radio,
  RadioGroup,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { MembershipType } from '@/components/admin/membership/users/components/MembershipTypeMenu';
import {
  EGroupConditionLogic,
  EGroupFindPermission,
  EGroupSelfJoinPermission,
} from '@/graphql-types/globalTypes';
import { InfoIcon } from '@/imports/ui/chakra/feather';
import { getPermissionLabel } from '@/utilities';

import {
  contactAttributesAreSelected,
  formatGroupAttributeValuesForDisplay,
} from '../../../groupAttributeUtils';
import JoinConfigSelection from './components/JoinConfigSelection';

// Options for additional checkboxes
const extraOptions = [
  {
    label: 'Contacts with selected People Attributes',
    value: 'people_attributes',
  },
  {
    label: 'Contacts with selected Company Attributes',
    value: 'company_attributes',
  },
  {
    label: 'Members with selected Membership Type',
    value: 'membership_type',
  },
];
export const CanJoinConfig: React.FC<{
  isEditing: boolean;
  availablePermissions: Map<
    EGroupFindPermission,
    {
      selfJoinPermissions: EGroupSelfJoinPermission[];
    }
  >;
  selectedPeopleAttributes?: string[];
  formState: any;
  configSelection?: string[];
  onRequestJoinChange?: (
    newValue: EGroupSelfJoinPermission
  ) => Promise<boolean>;
  isJoinChangeLoading?: boolean;
}> = ({
  isEditing,
  availablePermissions,
  selectedPeopleAttributes,
  formState,
  onRequestJoinChange,
  isJoinChangeLoading,
}) => {
  const { watch, setValue, clearErrors } = useFormContext();
  const findConfigValue = watch('canFindConfig');
  const joinConfigValue = watch('canJoinConfig');
  const allowedJoinValues =
    availablePermissions.get(findConfigValue)?.selfJoinPermissions;
  const peopleAttributes = watch('peopleAttributes');
  const companyAttributes = watch('companyAttributes');
  const membershipTypeIds = watch('membershipTypeIds');
  const configSelectionRaw = watch('configSelection');
  const configSelection = Array.isArray(configSelectionRaw)
    ? configSelectionRaw
    : [];
  const isGlobalSingleton = watch('isGlobalSingleton');

  const JOIN_TOOLTIPS: Partial<Record<EGroupSelfJoinPermission, string>> = {
    [EGroupSelfJoinPermission.LOGGED_IN]:
      'Any logged-in user can request to join this group.',
    [EGroupSelfJoinPermission.MEMBER_STATUS_USERS]:
      'Only users with member status can join. Non-members will be removed from this group automatically.',
    [EGroupSelfJoinPermission.ADMINS]:
      'Only admins can add or invite users to this group.',
    [EGroupSelfJoinPermission.NOBODY]:
      'The group is closed to new members unless manually added by an admin.',
  };

  const clearJoinConditions = React.useCallback(() => {
    setValue('configSelection', []);
    setValue('peopleAttributes', {
      defaultAttributes: [],
      customAttributes: [],
    });
    setValue('companyAttributes', {
      defaultAttributes: [],
      customAttributes: [],
    });
    setValue('membershipTypeIds', []);
    setValue('conditionLogic', EGroupConditionLogic.OR);
    clearErrors('peopleAttributes');
  }, [clearErrors, setValue]);

  const applyJoinChange = React.useCallback(
    (newValue: EGroupSelfJoinPermission | undefined) => {
      setValue('canJoinConfig', newValue);
      const supportsConditions =
        newValue === EGroupSelfJoinPermission.LOGGED_IN ||
        newValue === EGroupSelfJoinPermission.MEMBER_STATUS_USERS;
      if (!supportsConditions) {
        clearJoinConditions();
      }
    },
    [clearJoinConditions, setValue]
  );

  const requestJoinChange = React.useCallback(
    async (newValue: EGroupSelfJoinPermission) => {
      if (newValue === joinConfigValue || isJoinChangeLoading) {
        return;
      }
      if (!onRequestJoinChange) {
        applyJoinChange(newValue);
        return;
      }
      const accepted = await onRequestJoinChange(newValue);
      if (accepted) {
        applyJoinChange(newValue);
      }
    },
    [
      applyJoinChange,
      isJoinChangeLoading,
      joinConfigValue,
      onRequestJoinChange,
    ]
  );

  React.useEffect(() => {
    if (!configSelection.includes('people_attributes')) {
      clearErrors('peopleAttributes');
    }
  }, [configSelection, clearErrors]);

  React.useEffect(() => {
    if (!allowedJoinValues || allowedJoinValues.length === 0) {
      setValue('canJoinConfig', undefined);
      return;
    }
    if (!allowedJoinValues.includes(joinConfigValue)) {
      const nextJoin = allowedJoinValues[0];
      setValue('canJoinConfig', nextJoin);
      if (
        nextJoin !== EGroupSelfJoinPermission.LOGGED_IN &&
        nextJoin !== EGroupSelfJoinPermission.MEMBER_STATUS_USERS
      ) {
        clearJoinConditions();
      }
    }
  }, [allowedJoinValues, clearJoinConditions, joinConfigValue, setValue]);

  if (!allowedJoinValues || allowedJoinValues.length === 0) {
    return null;
  }
  const isPeopleAttributeAvailable =
    contactAttributesAreSelected(peopleAttributes);

  const isCompanyAttributeAvailable =
    contactAttributesAreSelected(companyAttributes);

  const isMembershipTypeAvailable = (membershipTypeIds?.length ?? 0) > 0;

  return (
    <Box w="full">
    <FormControl as="fieldset" w="full">
      <FormLabel as="legend">Who Can Join This Group?</FormLabel>
      {isEditing ? (
        <>
          {/* Radio Group */}
          <FormLabel as="legend">Choose Roles</FormLabel>

          <Controller
            name="canJoinConfig"
            render={({ field: { value, ref } }) => (
              <>
                <RadioGroup
                  value={value}
                  onChange={(newValue) => {
                    void requestJoinChange(
                      newValue as EGroupSelfJoinPermission
                    );
                  }}
                  ref={ref}
                  isDisabled={isJoinChangeLoading}
                >
                  <VStack spacing="2" width="full" alignItems="start">
                    {allowedJoinValues.map((detail) => (
                      <Radio
                        key={detail}
                        aria-label={
                          getPermissionLabel({ selfJoin: detail }) || ''
                        }
                        value={detail}
                      >
                        <HStack spacing="1">
                          <Text as="span">
                            {getPermissionLabel({ selfJoin: detail })}
                          </Text>
                          <Tooltip label={JOIN_TOOLTIPS[detail] || ''}>
                            <InfoIcon color="blue.500" />
                          </Tooltip>
                        </HStack>
                      </Radio>
                    ))}
                  </VStack>
                </RadioGroup>
                {isJoinChangeLoading && (
                  <Text color="gray.500" mt="2">
                    Checking who would be affected…
                  </Text>
                )}
              </>
            )}
          />
        </>
      ) : (
        <>
          <Text color="gray.600">
            {getPermissionLabel({ selfJoin: joinConfigValue })}
          </Text>
          {/* Show selected checkboxes in non-edit mode */}
          {(joinConfigValue === 'LOGGED_IN' ||
            joinConfigValue === 'MEMBER_STATUS_USERS') &&
            watch('joinExtraOptions')?.length > 0 && (
              <Text color="gray.600" mt="2">
                {watch('joinExtraOptions')
                  .map(
                    (selected: string) =>
                      extraOptions.find((opt) => opt.value === selected)?.label
                  )
                  .join(', ')}
              </Text>
            )}
          {isPeopleAttributeAvailable && (
            <Box ml={5} mt={3} key="people_attribute">
              <FormLabel as="legend" mb={0}>
                People Attributes
              </FormLabel>
              {peopleAttributes?.defaultAttributes?.map(
                (ele: {
                  _id?: string;
                  values: string[];
                  name?: string;
                  schemaName?: string | null;
                  displayValues?: string[];
                  options?: { id: string; label: string }[];
                }) => {
                  const tagValues = formatGroupAttributeValuesForDisplay(ele);
                  return (
                    <Text as="legend" ml={5} key={ele._id || ele.name}>
                      {ele.schemaName || ele.name} : {tagValues.join(', ')}
                    </Text>
                  );
                },
              )}
              {peopleAttributes?.customAttributes?.map(
                (ele: {
                  schemaId: string;
                  values: string[];
                  name?: string;
                  schemaName?: string | null;
                  displayValues?: string[];
                  options?: { id: string; label: string }[];
                }) => {
                  const tagValues = formatGroupAttributeValuesForDisplay(ele);
                  return (
                    <Text as="legend" ml={5} key={ele.schemaId}>
                      {ele.schemaName || ele.name} : {tagValues.join(', ')}
                    </Text>
                  );
                },
              )}
            </Box>
          )}
          {isCompanyAttributeAvailable && (
            <Box ml={5} mt={3} key="company_attribute">
              <FormLabel as="legend" m={0}>
                Company Attributes
              </FormLabel>
              {companyAttributes?.defaultAttributes?.map(
                (ele: {
                  _id?: string;
                  values: string[];
                  name?: string;
                  schemaName?: string | null;
                  displayValues?: string[];
                  options?: { id: string; label: string }[];
                }) => {
                  const tagValues = formatGroupAttributeValuesForDisplay(ele);
                  return (
                    <Text as="legend" ml={5} key={ele._id || ele.name}>
                      {ele.schemaName || ele.name} : {tagValues.join(', ')}
                    </Text>
                  );
                },
              )}
              {companyAttributes?.customAttributes?.map(
                (ele: {
                  schemaId: string;
                  values: string[];
                  name?: string;
                  schemaName?: string | null;
                  displayValues?: string[];
                  options?: { id: string; label: string }[];
                }) => {
                  const tagValues = formatGroupAttributeValuesForDisplay(ele);
                  return (
                    <Text as="legend" ml={5} key={ele.schemaId}>
                      {ele.schemaName || ele.name} : {tagValues.join(', ')}
                    </Text>
                  );
                },
              )}
            </Box>
          )}
          {isMembershipTypeAvailable && (
            <Box ml={5} mt={3} key="membership_type">
              <FormLabel as="legend" m={0}>
                Membership Type
              </FormLabel>
              {membershipTypeIds?.map((ele: MembershipType) => (
                <Text as="legend" ml={5} key={ele._id}>
                  {ele.name}
                </Text>
              ))}
            </Box>
          )}
          {(isPeopleAttributeAvailable ||
            isCompanyAttributeAvailable ||
            isMembershipTypeAvailable) && (
            <Box ml={5} mt={3} key="condition_logic">
              <FormLabel as="legend" m={0}>
                Condition Logic
              </FormLabel>
              <Text as="legend" ml={5}>
                {watch('conditionLogic') === EGroupConditionLogic.AND
                  ? 'Match all selected attributes (AND)'
                  : 'Match any selected attribute (OR)'}
              </Text>
            </Box>
          )}
        </>
      )}
    </FormControl>
      {(joinConfigValue === 'LOGGED_IN' ||
        joinConfigValue === 'MEMBER_STATUS_USERS') &&
        !isGlobalSingleton &&
        isEditing && (
          <Box mt={4} w="full">
            <FormLabel as="legend">Select Conditions</FormLabel>
            <JoinConfigSelection />
          </Box>
        )}
    </Box>
  );
};
